import { FC, useMemo, useState } from 'react'
import { QuestionMarkTooltip } from 'src/components/QuestionMarkTooltip'
import { CommentsDonutChart } from 'src/charts/CommentsChartSection/CommentsDonutChart'
import { SENTIMENT_COLORS } from 'src/constants/sentimentColors'
import {
  BackendSentiment,
  CampaignCommentsData,
  useGetCampaignCommentsQuery,
} from 'src/reactQuery/useGetCampaignCommentsQuery'
import styles from './campaignCommentsSection.module.scss'
import { CampaignCommentsList, SentimentTab } from './CampaignCommentsList'

interface CampaignCommentsSectionProps {
  campaignId: string
  fromDate?: string
  // Opens Post Details for the mention a comment belongs to.
  onViewThreadDetails?: (threadId: string) => void
  toDate?: string
}

const EMPTY_DATA: CampaignCommentsData = {
  allSources: [],
  campaigns: [],
  comments: [],
  limit: 50,
  page: 0,
  sentimentBreakdown: {
    negative: 0,
    negativePercentage: 0,
    neutral: 0,
    neutralPercentage: 0,
    positive: 0,
    positivePercentage: 0,
  },
  totalComments: 0,
}

const TAB_TO_SENTIMENT: Record<SentimentTab, BackendSentiment | undefined> = {
  all: undefined,
  negative: 'NEG',
  neutral: 'NEU',
  positive: 'POS',
}

/**
 * Comments Chart for the social-listening campaign page — the same widget
 * Content Analysis shows, fed by /analytics/fetchComments (comments the
 * scrapers, or the admin page, collected for the posts this campaign
 * mentions).
 *
 * The sentiment tab and the platform dropdown are sent to the backend; the
 * donut reads the breakdown, which the backend computes without the sentiment
 * filter so the ring stays whole while the list narrows.
 */
export const CampaignCommentsSection: FC<CampaignCommentsSectionProps> = ({
  campaignId,
  fromDate,
  onViewThreadDetails,
  toDate,
}) => {
  const [activeTab, setActiveTab] = useState<SentimentTab>('all')
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all')

  const { data, isLoading } = useGetCampaignCommentsQuery({
    campaigns: campaignId,
    fromDate,
    limit: 100,
    selectedSources: selectedPlatform === 'all' ? undefined : selectedPlatform,
    sentiment: TAB_TO_SENTIMENT[activeTab],
    toDate,
  })

  // The donut and the dropdown options must not react to the tab or to the
  // platform filter, or the ring would shrink to one slice and the platform
  // list to one entry as soon as either is picked. They come from an
  // unfiltered read of the same campaign.
  const { data: unfiltered } = useGetCampaignCommentsQuery({
    campaigns: campaignId,
    fromDate,
    limit: 1,
    toDate,
  })

  const donutData = unfiltered || data || EMPTY_DATA
  const comments = data?.comments || []

  const platforms = useMemo(
    () => (unfiltered?.allSources || []).map((s) => s.source),
    [unfiltered]
  )

  const hasFilter = activeTab !== 'all' || selectedPlatform !== 'all'

  // MVP stand-in for a feature flag: a campaign with no collected comments does
  // not show the widget at all, so real customers see nothing until the scrape
  // pipeline fills it.
  if (!isLoading && !hasFilter && donutData.totalComments === 0) {
    return null
  }

  return (
    <div className={styles.campaignCommentsSection}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <span className={styles.title}>Comments Chart</span>
          <QuestionMarkTooltip
            overlay="Sentiment of the comments left on the posts this campaign mentions"
            placement="top"
          />
        </div>
      </div>

      <div className={styles.content}>
        <CommentsDonutChart data={donutData} isLoading={isLoading} />
        <CampaignCommentsList
          activeTab={activeTab}
          comments={comments}
          isLoading={isLoading}
          onPlatformChange={setSelectedPlatform}
          onTabChange={setActiveTab}
          onViewThreadDetails={onViewThreadDetails}
          platforms={platforms}
          selectedPlatform={selectedPlatform}
        />
      </div>

      <div className={styles.legend}>
        {(['positive', 'neutral', 'negative'] as const).map((key) => (
          <div className={styles.legendItem} key={key}>
            <span
              className={styles.legendDot}
              style={{ backgroundColor: SENTIMENT_COLORS[key] }}
            />
            <span className={styles.legendLabel}>
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
